import { useMemo, useState } from 'react';
import {
  AlertTriangle,
  BarChart3,
  ChevronDown,
  CircleDot,
  Flame,
  Info,
  Sparkles,
  Target,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { FinanceV2Section } from './FinanceV2Section';
import {
  ActionableInsight,
  ActionableInsightsBlockData,
  ActionablePriority,
  ActionableSupportingEvidence,
} from '../../types/dashboard';

/**
 * Executive-grade actionable insight cards — designed for a C-level
 * reader who will spend 3-5 seconds on each card before deciding
 * whether to drill in.
 *
 * Layout principle: the card is visual first, prose second.
 *   * Collapsed (default) — priority chip + driver tag + the title.
 *     Single row a CFO can scan vertically through the stack.
 *   * Expanded             — title + hero action + compact ``Why`` and
 *                            ``Impact`` rows + evidence chips inline
 *                            (no second-level disclosure). Everything
 *                            fits on one screen-height; no wall of
 *                            labeled paragraphs.
 *
 * The FE never invents text — every field rendered here comes
 * verbatim from the backend ``ActionableInsight`` object. Empty
 * fields hide their row (no placeholder "—" or "N/A").
 */

interface ActionableInsightsSectionProps {
  data?: ActionableInsightsBlockData | Record<string, unknown> | null;
  title?: string;
  framed?: boolean;
  onViewEvidence?: (evidence: ActionableSupportingEvidence) => void;
}

const priorityStyles: Record<ActionablePriority, {
  label: string;
  bullet: string;
  ring: string;
  stripe: string;
  chipBg: string;
  chipText: string;
  Icon: typeof Flame;
}> = {
  high: {
    label: 'High',
    bullet: 'bg-[#B23A48]',
    ring: 'hover:border-[#B23A48]/40',
    stripe: 'bg-gradient-to-b from-[#B23A48] to-[#8E2A36]',
    chipBg: 'bg-[#FBE7E7]',
    chipText: 'text-[#7E1D29]',
    Icon: Flame,
  },
  medium: {
    label: 'Medium',
    bullet: 'bg-[#C98661]',
    ring: 'hover:border-[#C98661]/40',
    stripe: 'bg-gradient-to-b from-[#C98661] to-[#B46647]',
    chipBg: 'bg-[#FBEEDC]',
    chipText: 'text-[#8B4A2B]',
    Icon: AlertTriangle,
  },
  low: {
    label: 'Low',
    bullet: 'bg-[#4E7C89]',
    ring: 'hover:border-[#4E7C89]/40',
    stripe: 'bg-gradient-to-b from-[#4E7C89] to-[#3D6470]',
    chipBg: 'bg-[#E6EEF1]',
    chipText: 'text-[#2C4A56]',
    Icon: Info,
  },
};

// Cosmetic-only transform — render whatever the backend sends with the
// underscores swapped for spaces. The label sits inside an
// ``uppercase tracking-wide`` class so the visual treatment is uniform
// regardless of source string.
const formatDriver = (driver: string): string =>
  (driver || '').replace(/_/g, ' ');

const evidenceIcon: Record<string, typeof Target> = {
  kpi_card:  Target,
  chart:     BarChart3,
  finding:   CircleDot,
  lens_item: Sparkles,
  narrative: Info,
};

// ---------------------------------------------------------------------------
// Normalization helpers
// ---------------------------------------------------------------------------

function asInsightsBlock(data?: ActionableInsightsBlockData | Record<string, unknown> | null): ActionableInsightsBlockData | null {
  if (!data || typeof data !== 'object') return null;
  const raw = data as Record<string, unknown>;
  const items = Array.isArray(raw.items) ? (raw.items as ActionableInsight[]) : [];
  if (items.length === 0 && Array.isArray(raw)) {
    const arr = raw as unknown as ActionableInsight[];
    return wrapFlatList(arr);
  }
  if (items.length === 0) return null;
  const by_priority =
    raw.by_priority && typeof raw.by_priority === 'object'
      ? (raw.by_priority as ActionableInsightsBlockData['by_priority'])
      : bucketByPriority(items);
  const counts =
    raw.counts && typeof raw.counts === 'object'
      ? (raw.counts as ActionableInsightsBlockData['counts'])
      : {
          high:   by_priority.high.length,
          medium: by_priority.medium.length,
          low:    by_priority.low.length,
          total:  items.length,
        };
  return { items, by_priority, counts };
}

function wrapFlatList(items: ActionableInsight[]): ActionableInsightsBlockData {
  const by_priority = bucketByPriority(items);
  return {
    items,
    by_priority,
    counts: {
      high: by_priority.high.length,
      medium: by_priority.medium.length,
      low: by_priority.low.length,
      total: items.length,
    },
  };
}

function bucketByPriority(items: ActionableInsight[]): ActionableInsightsBlockData['by_priority'] {
  const out: ActionableInsightsBlockData['by_priority'] = { high: [], medium: [], low: [] };
  items.forEach((item) => {
    const p = (item.priority || 'medium').toLowerCase() as ActionablePriority;
    if (p === 'high' || p === 'medium' || p === 'low') out[p].push(item);
    else out.medium.push(item);
  });
  return out;
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Evidence row — vertical list, full label visible (no truncation), with the
// rationale rendered inline below the label when present. Designed so the
// "Backed by" surface is FULLY SCANNABLE without hover.
// ---------------------------------------------------------------------------

function EvidenceRow({
  evidence,
  onViewEvidence,
}: {
  evidence: ActionableSupportingEvidence;
  onViewEvidence?: (evidence: ActionableSupportingEvidence) => void;
}) {
  const Icon = evidenceIcon[evidence.kind] || Info;
  const kindLabel = (
    {
      kpi_card:  'KPI',
      chart:     'Chart',
      finding:   'Finding',
      lens_item: 'Lens',
      narrative: 'Narrative',
    } as Record<string, string>
  )[evidence.kind] || evidence.kind;
  const interactive = Boolean(onViewEvidence);
  const sharedClassName = cn(
    'group/evidence flex w-full items-start gap-3 rounded-[10px] border border-[#EDE6E1] bg-white/70 px-3 py-2.5 text-left',
    interactive && 'cursor-pointer transition-colors hover:border-[#C8B89B] hover:bg-white'
  );
  const body = (
    <>
      <span
        className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#F5EFE6] text-[#6E3450]"
        aria-hidden="true"
      >
        <Icon className="h-3.5 w-3.5" strokeWidth={2.25} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="font-display text-[9.5px] font-semibold uppercase tracking-[0.18em] text-[#A8989F]">
            {kindLabel}
          </span>
          <span className="text-[12.5px] font-medium leading-snug text-[#1F1A20]">
            {evidence.label}
          </span>
        </div>
        {evidence.rationale && (
          <p className="mt-1 text-[11.5px] leading-relaxed text-[#5A4E54]">
            {evidence.rationale}
          </p>
        )}
      </div>
    </>
  );
  if (interactive) {
    return (
      <button
        type="button"
        onClick={() => onViewEvidence?.(evidence)}
        className={sharedClassName}
      >
        {body}
      </button>
    );
  }
  return <div className={sharedClassName}>{body}</div>;
}

// ---------------------------------------------------------------------------
// Card — the recommended action is the HERO. Collapsed shows the action
// itself (not the title); the title becomes the "what this addresses" line
// once expanded. This reorders priority around what the C-level reader
// actually needs to act on.
// ---------------------------------------------------------------------------

function ActionableCard({
  insight,
  onViewEvidence,
}: {
  insight: ActionableInsight;
  onViewEvidence?: (evidence: ActionableSupportingEvidence) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  const priorityKey = ((insight.priority || 'medium').toLowerCase() as ActionablePriority);
  const style = priorityStyles[priorityKey] || priorityStyles.medium;
  const PrioIcon = style.Icon;
  const driver = formatDriver(insight.root_cause_driver);

  return (
    <article
      className={cn(
        'relative overflow-hidden rounded-[14px] border border-[#EDE6E1] bg-[#FBF9F4]/50 transition-all duration-200 ease-out',
        style.ring,
        expanded && 'bg-[#FBF9F4] shadow-[0_1px_0_0_rgba(31,26,32,0.04),0_8px_24px_-12px_rgba(110,52,80,0.18)]'
      )}
    >
      {/* Priority accent stripe — full card height, gradient. */}
      <span className={cn('absolute inset-y-0 left-0 w-[5px]', style.stripe)} aria-hidden="true" />

      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={`actionable-body-${insight.id}`}
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-start gap-3 px-5 py-4 pl-6 text-left transition-colors hover:bg-[#FBF9F4]/70"
      >
        <div className="min-w-0 flex-1">
          {/* Meta row — priority + driver + confidence */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span
              className={cn(
                'inline-flex items-center gap-1 rounded-full px-2 py-[3px] font-display text-[10px] font-semibold uppercase tracking-[0.16em]',
                style.chipBg,
                style.chipText
              )}
            >
              <PrioIcon className="h-3 w-3" strokeWidth={2.5} />
              <span>{style.label}</span>
            </span>
            <span className="font-display text-[10px] uppercase tracking-[0.16em] text-[#7B6B72]">
              {driver}
            </span>
            <span className="text-[10px] text-[#C8B89B]">·</span>
            <span className="font-display text-[10px] uppercase tracking-[0.16em] text-[#A8989F]">
              {insight.confidence} confidence
            </span>
          </div>

          {/* Hero action — the headline. Always the prominent text on the
              card whether collapsed or expanded. Display-serif for the
              editorial weight; the action verb leads the reader. */}
          {insight.recommended_action && (
            <h3 className="mt-2 font-display text-[16px] font-semibold leading-snug text-[#1F1A20]">
              {insight.recommended_action}
            </h3>
          )}
        </div>

        <ChevronDown
          className={cn(
            'mt-1 h-4 w-4 shrink-0 text-[#7B6B72] transition-transform duration-200 ease-out',
            expanded && 'rotate-180'
          )}
          strokeWidth={2.25}
        />
      </button>

      <div
        id={`actionable-body-${insight.id}`}
        className={cn(
          'grid px-6 transition-all duration-250 ease-out',
          expanded ? 'grid-rows-[1fr] pb-5 opacity-100' : 'grid-rows-[0fr] opacity-0'
        )}
        aria-hidden={!expanded}
      >
        <div className="overflow-hidden">
          <div className="space-y-5 border-t border-dashed border-[#E5DACC] pt-4">

            {/* Context — title (CFO-grade headline of the problem the
                action addresses) + why (root_cause) + qualitative impact.
                Two-column dl on wider screens; stacks naturally on mobile. */}
            {(insight.title || insight.root_cause || insight.estimated_business_impact) && (
              <dl className="grid grid-cols-1 gap-x-6 gap-y-2.5 text-[13px] sm:grid-cols-[96px_1fr]">
                {insight.title && (
                  <>
                    <dt className="font-display text-[10.5px] font-semibold uppercase tracking-[0.16em] text-[#7B6B72] sm:pt-[2px]">
                      Context
                    </dt>
                    <dd className="leading-relaxed text-[#3F3338]">{insight.title}</dd>
                  </>
                )}
                {insight.root_cause && (
                  <>
                    <dt className="font-display text-[10.5px] font-semibold uppercase tracking-[0.16em] text-[#7B6B72] sm:pt-[2px]">
                      Why
                    </dt>
                    <dd className="leading-relaxed text-[#3F3338]">{insight.root_cause}</dd>
                  </>
                )}
                {insight.estimated_business_impact && (
                  <>
                    <dt className="font-display text-[10.5px] font-semibold uppercase tracking-[0.16em] text-[#7B6B72] sm:pt-[2px]">
                      Impact
                    </dt>
                    <dd className="leading-relaxed text-[#3F3338]">{insight.estimated_business_impact}</dd>
                  </>
                )}
              </dl>
            )}

            {/* Backed by — full evidence list with labels and rationales
                visible without hover. Vertical layout so every label can
                wrap to multiple lines and stay legible. */}
            {insight.supporting_evidence.length > 0 && (
              <div className="border-t border-dashed border-[#E5DACC] pt-4">
                <div className="mb-2.5 flex items-baseline gap-2">
                  <span className="font-display text-[10.5px] font-semibold uppercase tracking-[0.18em] text-[#7B6B72]">
                    Backed by
                  </span>
                  <span className="font-mono text-[10px] tabular-nums text-[#A8989F]">
                    {insight.supporting_evidence.length}
                  </span>
                </div>
                <ul className="space-y-1.5">
                  {insight.supporting_evidence.map((ev, idx) => (
                    <li key={`${ev.kind}-${ev.ref_id}-${idx}`}>
                      <EvidenceRow evidence={ev} onViewEvidence={onViewEvidence} />
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------
// Section
// ---------------------------------------------------------------------------

export function ActionableInsightsSection({
  data,
  title = 'Actionable Insights',
  framed = false,
  onViewEvidence,
}: ActionableInsightsSectionProps) {
  const normalized = useMemo(() => asInsightsBlock(data), [data]);

  if (!normalized || normalized.counts.total === 0) return null;

  const sections: Array<{ key: ActionablePriority; items: ActionableInsight[] }> = [
    { key: 'high', items: normalized.by_priority.high },
    { key: 'medium', items: normalized.by_priority.medium },
    { key: 'low', items: normalized.by_priority.low },
  ];

  const content = (
    <div className="flex flex-col gap-5">
      <p className="text-[12px] italic leading-relaxed text-[#7B6B72]">
        Each card leads with the action to take. Open one to see the context, the why, the expected impact, and the evidence behind it.
      </p>
      {sections.map(({ key, items }) => {
        if (items.length === 0) return null;
        const style = priorityStyles[key];
        return (
          <section key={key}>
            <div className="mb-2.5 flex items-baseline gap-2">
              <span className={cn('h-[3px] w-10 rounded-full', style.bullet)} />
              <h2 className="font-display text-[11px] font-semibold uppercase tracking-[0.16em] text-[#3F3338]">
                {style.label} Priority
              </h2>
              <span className="font-mono text-[10.5px] tabular-nums text-[#A8989F]">
                {items.length}
              </span>
            </div>
            <ul className="space-y-2.5">
              {items.map((insight) => (
                <li key={insight.id}>
                  <ActionableCard insight={insight} onViewEvidence={onViewEvidence} />
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );

  if (framed) {
    return <FinanceV2Section title={title}>{content}</FinanceV2Section>;
  }
  return content;
}

export default ActionableInsightsSection;
